/**
 * Max-Value Strategy for Best-Fit Processing
 *
 * Implements the traditional best-fit approach that maximizes total principal capacity.
 * This strategy migrates the existing logic from traditional-search.ts and provides
 * the same behavior as the original best-fit processing for non-target scenarios.
 *
 * Optimization Goal: Maximize total principal (bond value/capacity)
 * Search Method: Forward-only progression through millage shapes
 * Stopping Criteria: When total principal improvement stops
 */

import { OPTIMIZATION_CONSTANTS } from '~/config/financial-constants';
import { getShapePosition } from '~/engine/millage/shape-progression';
import { BondCategorizationEngine } from '~/engine/processing/shared/bond-categorization';
import { logger } from '~/services/logging';
import type { Candidate, Configuration } from '~/types';
import { BaseBestFitStrategy } from './base-strategy';
import { ComparisonEngine } from './comparison-engine';
import {
  getBestCandidate,
  getFirstFitCandidates,
  getStandardViableCandidates,
} from './shared-utilities';
import {
  ProjectType,
  type SearchContext,
  type SearchResult,
  type SearchStoppingReason,
} from './strategy-types';

/**
 * Max-Value Strategy Implementation
 *
 * This strategy focuses on maximizing the total principal amount that can be bonded,
 * making it ideal for projects where the goal is to maximize debt capacity utilization.
 */
export class MaxValueStrategy extends BaseBestFitStrategy {
  readonly strategyType = ProjectType.MAX_VALUE;
  readonly strategyName = 'Max-Value Strategy';
  readonly description = 'Maximize total principal capacity';

  /**
   * Max-value viability uses standard criteria: DRF > 0 and basic financial viability.
   */
  isViable(candidate: Candidate): boolean {
    return candidate.results.isViable;
  }

  /**
   * Selects the first-fit candidate with the highest total principal.
   * This gives us the best starting point for maximizing capacity.
   */
  selectBestFirstFit(candidates: Candidate[], configuration: Configuration): Candidate | null {
    const firstFitCandidates = getFirstFitCandidates(candidates);
    const viableCandidates = getStandardViableCandidates(firstFitCandidates);

    if (viableCandidates.length === 0) {
      return null;
    }

    // Use comparison engine to get the candidate with highest principal
    const strategy = BondCategorizationEngine.determineOrderingStrategy(
      viableCandidates,
      configuration
    );
    const sortFunction = ComparisonEngine.getSortFunctionForStrategy(strategy);
    const bestCandidate = getBestCandidate(viableCandidates, sortFunction);

    if (bestCandidate) {
      logger.L3.info(
        `🎯 Max-Value: Selected ${bestCandidate.name} (Principal: $${bestCandidate.results.totalPrincipal.toLocaleString()})`
      );
    }

    return bestCandidate;
  }

  /**
   * Executes forward-only search maximizing total principal.
   * Continues until total principal stops improving or other stopping conditions are met.
   */
  async executeSearch(startingCandidate: Candidate, context: SearchContext): Promise<SearchResult> {
    const startTime = Date.now();
    // Note: baseElements, configuration, projectData available in context if needed
    const maxCandidates = 50;
    const improvementThreshold = OPTIMIZATION_CONSTANTS.MAX_VALUE_IMPROVEMENT_THRESHOLD;

    this.logStrategyStart(1, startingCandidate);

    let bestCandidateSoFar = startingCandidate;
    let currentShapeId = startingCandidate.millageInformation.millageShape.shapeId;
    const allCandidates: Candidate[] = [];
    const searchPath: string[] = [currentShapeId];
    let candidatesGenerated = 0;

    logger.L3.info(
      `🔍 Starting from ${startingCandidate.name} with $${startingCandidate.results.totalPrincipal.toLocaleString()}`
    );

    // Check if we're already at the last shape (top of the range)
    const shapePosition = getShapePosition(currentShapeId);
    if (shapePosition.isLast) {
      logger.L3.info(
        `🎯 Already at the highest shape (${currentShapeId}) - skipping best-fit search`
      );
      const processingTime = Date.now() - startTime;
      const searchDetails = this.createSearchDetails(
        startingCandidate,
        0,
        'forward',
        this.getPrimaryMetric(bestCandidateSoFar),
        0
      );

      const result = this.createSearchResult(
        bestCandidateSoFar,
        allCandidates,
        searchPath,
        'already_at_maximum' as SearchStoppingReason,
        searchDetails,
        processingTime
      );

      this.logStrategyComplete(result);
      return result;
    }

    while (candidatesGenerated < maxCandidates) {
      // Get the next shape in the progression
      const nextShapeId = this.getNextShape(currentShapeId, 'forward');

      if (!nextShapeId) {
        logger.L3.debug(`🔍 Search stopped: No more shapes available after ${currentShapeId}`);
        const processingTime = Date.now() - startTime;
        const searchDetails = this.createSearchDetails(
          startingCandidate,
          candidatesGenerated,
          'forward',
          this.getPrimaryMetric(bestCandidateSoFar),
          this.getPrimaryMetric(bestCandidateSoFar) - this.getPrimaryMetric(startingCandidate)
        );

        const result = this.createSearchResult(
          bestCandidateSoFar,
          allCandidates,
          searchPath,
          'no_more_shapes',
          searchDetails,
          processingTime
        );

        this.logStrategyComplete(result);
        return result;
      }

      // Generate and process the next candidate
      const nextCandidate = this.createCandidateFromShape(context, nextShapeId);

      if (!nextCandidate) {
        logger.L3.debug(`🔍 Search stopped: Could not create candidate for shape ${nextShapeId}`);

        const searchDetails = this.createSearchDetails(
          startingCandidate,
          candidatesGenerated,
          'forward',
          this.getPrimaryMetric(bestCandidateSoFar),
          this.getPrimaryMetric(bestCandidateSoFar) - this.getPrimaryMetric(startingCandidate)
        );

        const result = this.createSearchResult(
          bestCandidateSoFar,
          allCandidates,
          searchPath,
          'candidate_not_viable',
          searchDetails,
          Date.now() - startTime
        );

        this.logStrategyComplete(result);
        return result;
      }

      // Check viability BEFORE adding to results
      if (!this.isViable(nextCandidate)) {
        searchPath.push(nextShapeId); // Still track that we tried this shape
        candidatesGenerated++;

        logger.L3.debug(`🔍 ${nextCandidate.name}: Not viable - stopping search`);

        // Don't add non-viable candidate to results
        const processingTime = Date.now() - startTime;
        const searchDetails = this.createSearchDetails(
          startingCandidate,
          candidatesGenerated,
          'forward',
          this.getPrimaryMetric(bestCandidateSoFar),
          this.getPrimaryMetric(bestCandidateSoFar) - this.getPrimaryMetric(startingCandidate)
        );

        const result = this.createSearchResult(
          bestCandidateSoFar,
          allCandidates,
          searchPath,
          'candidate_not_viable',
          searchDetails,
          processingTime
        );

        this.logStrategyComplete(result);
        return result;
      }

      // Only add viable candidates to results
      allCandidates.push(nextCandidate);
      searchPath.push(nextShapeId);
      candidatesGenerated++;

      // Check if this candidate represents an improvement
      const currentPrincipal = this.getPrimaryMetric(bestCandidateSoFar);
      const nextPrincipal = this.getPrimaryMetric(nextCandidate);
      const improvement = nextPrincipal - currentPrincipal;

      this.logSearchProgress(
        nextCandidate.name,
        nextPrincipal,
        this.getPrimaryMetricName(),
        improvement
      );

      const strategy = BondCategorizationEngine.determineOrderingStrategy(
        [bestCandidateSoFar, nextCandidate],
        context.configuration
      );
      if (!ComparisonEngine.shouldContinueSearch(bestCandidateSoFar, nextCandidate, strategy)) {
        logger.L3.info(`🔍 Search stopped: Improvement below threshold ($${improvementThreshold})`);
        logger.L3.info(
          `🏆 Best candidate: ${bestCandidateSoFar.name} with $${currentPrincipal.toLocaleString()}`
        );

        const processingTime = Date.now() - startTime;
        const searchDetails = this.createSearchDetails(
          startingCandidate,
          candidatesGenerated,
          'forward',
          currentPrincipal,
          currentPrincipal - this.getPrimaryMetric(startingCandidate)
        );

        const result = this.createSearchResult(
          bestCandidateSoFar,
          allCandidates,
          searchPath,
          'improvement_stopped',
          searchDetails,
          processingTime
        );

        this.logStrategyComplete(result);
        return result;
      }

      // This candidate is better, continue the search
      bestCandidateSoFar = nextCandidate;
      currentShapeId = nextShapeId;

      logger.L3.debug(`✅ New best candidate: ${bestCandidateSoFar.name}`);
    }

    // Safety limit reached
    logger.L3.debug(`🔍 Search stopped: Maximum candidates limit (${maxCandidates}) reached`);

    // Add the starting candidate to allCandidates if not already there
    if (!allCandidates.find((c) => c.id === startingCandidate.id)) {
      allCandidates.unshift(startingCandidate);
    }

    // Re-select the best candidate from ALL candidates generated
    const actualBestCandidate = this.selectBestFromAll(allCandidates, context);

    const processingTime = Date.now() - startTime;
    const searchDetails = this.createSearchDetails(
      startingCandidate,
      candidatesGenerated,
      'forward',
      this.getPrimaryMetric(actualBestCandidate),
      this.getPrimaryMetric(actualBestCandidate) - this.getPrimaryMetric(startingCandidate)
    );

    const result = this.createSearchResult(
      actualBestCandidate,
      allCandidates,
      searchPath,
      'max_candidates_reached',
      searchDetails,
      processingTime
    );

    this.logStrategyComplete(result);
    return result;
  }

  /**
   * Selects the best candidate from all candidates generated during the search.
   * This ensures we pick the actual best, not just the last one tracked during search.
   */
  private selectBestFromAll(candidates: Candidate[], context: SearchContext): Candidate {
    const strategy = BondCategorizationEngine.determineOrderingStrategy(
      candidates,
      context.configuration
    );
    const sortFunction = ComparisonEngine.getSortFunctionForStrategy(strategy);
    const bestCandidate = getBestCandidate(candidates, sortFunction);

    if (!bestCandidate) {
      throw new Error('No best candidate found from all candidates');
    }

    logger.L3.info(
      `🎯 Re-selected best from all ${candidates.length} candidates: ${bestCandidate.name}`
    );
    return bestCandidate;
  }

  /**
   * Gets total principal as the primary metric for max-value strategy.
   */
  getPrimaryMetric(candidate: Candidate): number {
    return candidate.results.totalPrincipal;
  }

  /**
   * Returns human-readable name for the primary metric.
   */
  getPrimaryMetricName(): string {
    return 'Total Principal';
  }

  /**
   * Max-value strategy has no additional validation beyond the base class.
   */
  validateCandidates(candidates: Candidate[]): string | null {
    const baseValidation = super.validateCandidates(candidates);
    if (baseValidation) return baseValidation;

    // Max-value strategy works with any viable candidates
    return null;
  }
}
